const API_BASE = import.meta.env.VITE_API_URL || ''

class ApiError extends Error {
  // `body` is the parsed response, kept so a caller can read structured detail
  // the message cannot carry — a 409 that names the other record it clashed
  // with, for instance. Without it the only way to pass an id back would be to
  // write it into the message and have the UI parse it out again.
  constructor(message, status, isRetryable = false, body = null) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.isRetryable = isRetryable
    this.body = body
  }
}

// Convert an error payload (string | object | array | null) into a single
// human-readable string. Handles FastAPI's validation-error shape
// ([{ loc:[...], msg:'...', type:'...' }, ...]).
export function formatErrorPayload(payload) {
  if (payload == null) return null
  if (typeof payload === 'string') return payload
  if (Array.isArray(payload)) {
    const parts = payload.map(item => {
      if (typeof item === 'string') return item
      if (item && typeof item === 'object') {
        const loc = Array.isArray(item.loc) ? item.loc.filter(s => s !== 'body').join('.') : ''
        const msg = item.msg || JSON.stringify(item)
        return loc ? `${loc}: ${msg}` : msg
      }
      return String(item)
    }).filter(Boolean)
    return parts.length > 0 ? parts.join('; ') : null
  }
  if (typeof payload === 'object') {
    if (typeof payload.msg === 'string') return payload.msg
    if (typeof payload.message === 'string') return payload.message
    try { return JSON.stringify(payload) } catch { return null }
  }
  return String(payload)
}

class ApiClient {
  async request(endpoint, options = {}) {
    const url = `${API_BASE}${endpoint}`

    // FormData sets its own multipart Content-Type (with the boundary); forcing
    // application/json here would corrupt an upload, so let the browser own it.
    const isFormData = options.body instanceof FormData
    const headers = {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...options.headers
    }

    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include'
    })

    // Read body as text first, then attempt to parse as JSON. Avoids crashing
    // on edge-server error bodies (e.g. Cloudflare "error code: 1006") that
    // can arrive with a JSON content-type header but a plain-text body.
    const text = await response.text()
    let data
    if (text) {
      try {
        data = JSON.parse(text)
      } catch {
        data = { error: text }
      }
    } else {
      data = { error: `HTTP error ${response.status}` }
    }

    if (!response.ok) {
      // FastAPI 422 returns detail as an array of { loc, msg, type } objects;
      // some endpoints return detail as a single object. Normalise to a
      // string so callers don't end up rendering "[object Object]".
      const message =
        formatErrorPayload(data.detail) ||
        formatErrorPayload(data.error) ||
        `HTTP error ${response.status}`
      const isRetryable = response.status >= 500 || data.retry === true
      throw new ApiError(message, response.status, isRetryable, data)
    }

    return data
  }

  get(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'GET' })
  }

  post(endpoint, body, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(body)
    })
  }

  put(endpoint, body, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(body)
    })
  }

  patch(endpoint, body, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(body)
    })
  }

  delete(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'DELETE' })
  }

  // Multipart POST — body is a FormData; the browser sets the Content-Type
  // boundary (see request()). Used for photo uploads (KG image identify).
  postForm(endpoint, formData, options = {}) {
    return this.request(endpoint, { ...options, method: 'POST', body: formData })
  }
}

export const api = new ApiClient()
export { ApiError }

// Utility for formatting currency
export function formatCurrency(amount) {
  if (amount == null) return '-'
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP'
  }).format(amount)
}

// Format a minor-unit amount (cents/pence) with an explicit currency code.
// Used by APIs that return { total_cents, currency } pairs (e.g. printruns).
export function formatCurrencyFromCents(cents, currency = 'GBP') {
  if (cents == null) return '-'
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
  }).format(cents / 100)
}

// Format a whole-second count as a compact UK duration for the records page:
// under a minute → "under a minute"; sub-hour → "47m"; sub-day → "3h 12m";
// beyond a day → "2d 4h". Guards null/negative (returns "-") so a missing
// turnaround renders cleanly rather than as NaN.
export function formatDuration(totalSeconds) {
  if (totalSeconds == null || totalSeconds < 0) return '-'
  if (totalSeconds < 60) return 'under a minute'
  const mins = Math.floor(totalSeconds / 60)
  if (mins < 60) return `${mins}m`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ${mins % 60}m`
  const days = Math.floor(hours / 24)
  return `${days}d ${hours % 24}h`
}

// D1 writes datetime('now') / CURRENT_TIMESTAMP as "YYYY-MM-DD HH:MM:SS" in
// UTC with no zone marker. `new Date()` reads that shape as LOCAL time (and
// Safari refuses it outright), so a 12:05 UTC reply would show as 12:05 in
// London instead of 13:05. Read it as the UTC instant it is; pass anything
// else (ISO strings, Date objects) straight through. Null for no value or
// an unparseable one, never an Invalid Date.
const D1_DATETIME = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/

export function parseServerDate(value) {
  if (value == null || value === '') return null
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value
  const raw = String(value)
  const date = new Date(D1_DATETIME.test(raw) ? `${raw.replace(' ', 'T')}Z` : raw)
  return Number.isNaN(date.getTime()) ? null : date
}

// Utility for formatting dates
export function formatDate(dateString) {
  const date = parseServerDate(dateString)
  if (!date) return '-'
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  }).format(date)
}

// Time on its own, for tables that give the clock its own column so a day's
// worth of activity reads down the page without re-reading the date each row.
export function formatTimeOfDay(dateString) {
  const date = parseServerDate(dateString)
  if (!date) return '-'
  return new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit'
  }).format(date)
}

export function formatDateTime(dateString) {
  const date = parseServerDate(dateString)
  if (!date) return '-'
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date)
}

// "27 Oct 10:14": day, month and time, no year, for a line that says when.
export function formatDayMonthTime(dateString) {
  const date = parseServerDate(dateString)
  if (!date) return '-'
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date).replace(',', '')
}

// Status badge class helper
export function getStatusBadgeClass(status) {
  const statusMap = {
    // Deals
    quote_requested: 'badge-info',
    quote_ready: 'badge-info',
    quote_sent: 'badge-success',
    quote_expiring: 'badge-warning',
    awaiting_shipment: 'badge-info',
    shipping_arranged: 'badge-info',
    arrived_to_test: 'badge-info',
    tested: 'badge-success',
    issue_with_item: 'badge-error',
    awaiting_payment: 'badge-warning',
    closed_traded: 'badge-success',
    closed_lost: 'badge-neutral',

    // Inventory
    draft: 'badge-neutral',
    active: 'badge-success',
    sold: 'badge-info',
    archived: 'badge-neutral',

    // Orders
    pending: 'badge-warning',
    confirmed: 'badge-info',
    processing: 'badge-info',
    shipped: 'badge-success',
    delivered: 'badge-success',
    cancelled: 'badge-error',
    refunded: 'badge-neutral',

    // Warehouse
    receiving: 'badge-info',
    testing: 'badge-info',
    repairs: 'badge-warning',
    issue_trades: 'badge-error',
    putaway: 'badge-info',
    photography: 'badge-info',
    ready_to_ship: 'badge-success',

    // Repairs
    not_started: 'badge-neutral',
    in_progress: 'badge-info',
    complete: 'badge-success',
    not_repaired: 'badge-error',
    listed: 'badge-success'
  }

  return statusMap[status] || 'badge-neutral'
}

export function formatStatus(status) {
  if (!status) return '-'
  return status
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase())
}
